import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	BuilderContext,
	BuilderContextData
} from "./BuilderContext";
import { Layout } from "./Layout/Layout";
import { Toolbar } from "./Toolbar";
import { Guid } from "client_legacy/ndaw/guid";
import { isNotNil } from "client/Misc/utils";
import { HubItemImageRestrictionsModel } from "client/Api/Hub/common";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";

export type BuilderHandle = {
	hideToolbar(): void;
	getIsCurrentEditorChanged(): boolean;
};

export type BuilderProps = {
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	isPixabayImageGalleryAvailable: boolean;
};

const BuilderInternal = forwardRef<BuilderHandle, PropsWithChildren<BuilderProps>>(
	(props: PropsWithChildren<BuilderProps>, ref: Ref<BuilderHandle>): ReactElement => {
		const {
			imageRestrictions,
			defaultImageLocation,
			children,
			isPixabayImageGalleryAvailable
		} = props;

		const getHasChangesHandlesMapRef = useRef<Map<Guid, (() => boolean)>>(null!);

		if (!getHasChangesHandlesMapRef.current) {
			getHasChangesHandlesMapRef.current = new Map();
		}

		const [activeEditorId, setActiveEditorId] = useState<Guid | null>(null);
		const [toolbarContainer, setToolbarContainer] = useState<HTMLElement | null>(null);

		const toolbarContainerChangeHandler = useCallback(
			(element: HTMLElement | null): void => {
				setToolbarContainer(
					(current => {
						if (!element) {
							return element;
						}

						if (isNotNil(current) && current !== element) {
							throw new Error("Toolbar container is already set. Cannot have more then 1 toolbar container.");
						}

						return element;
					})
				);
			},
			[]
		);

		const getHasChangesHandleChangeHandler = useCallback(
			(id: Guid, handle: (() => boolean) | null): void => {
				const getHasChangesHandlesMap: Map<Guid, () => boolean> = getHasChangesHandlesMapRef.current;

				if (!handle) {
					getHasChangesHandlesMap.delete(id);
				} else {
					getHasChangesHandlesMap.set(id, handle);
				}
			},
			[]
		);

		const contextData = useMemo(
			(): BuilderContextData => ({
				activeEditorId,
				setActiveEditorId,
				toolbarContainer,
				setToolbarContainer: toolbarContainerChangeHandler,
				imageRestrictions,
				defaultImageLocation,
				setGetHasChangesHandle: getHasChangesHandleChangeHandler,
				isPixabayImageGalleryAvailable
			}),
			[
				activeEditorId,
				imageRestrictions,
				toolbarContainer,
				defaultImageLocation,
				toolbarContainerChangeHandler,
				getHasChangesHandleChangeHandler,
				isPixabayImageGalleryAvailable
			]
		);

		useImperativeHandle(
			ref,
			(): BuilderHandle => ({
				hideToolbar(): void {
					setActiveEditorId(null);
				},
				getIsCurrentEditorChanged(): boolean {
					if (!activeEditorId) {
						return false;
					}

					const handle: (() => boolean) | undefined = getHasChangesHandlesMapRef.current
						.get(activeEditorId);

					if (!handle) {
						return false;
					}

					return handle();
				},
			}),
			[activeEditorId]
		);

		return (
			<BuilderContext.Provider value={contextData}>
				{children}
			</BuilderContext.Provider>
		);
	}
);
BuilderInternal.displayName="Builder";

type BuilderType = typeof BuilderInternal & {
	Layout: typeof Layout;
	Toolbar: typeof Toolbar;
};

export const Builder = BuilderInternal as unknown as BuilderType;

Builder.Layout = Layout;
Builder.Toolbar = Toolbar;
